import React, { useState, useRef, useEffect } from "react";
import { TeamLogo } from "../common/TeamLogo";
import { ColorPicker } from "../settings/ColorPicker";

function TeamForm({ initialValues = {}, onSubmit, submitLabel = "SAVE", enableAvatar = false, onChange, isSubmitting = false }) {
  const fileInputRef = useRef(null);
  const [formState, setFormState] = useState({
    teamName: initialValues.teamName || "",
    kitColor: initialValues.kitColor || "#ffffff",
    logo: null, // File object
    logoPreview: initialValues.logoPreview || null, // string (data-url or URL)
    // Only used when enableAvatar === true
    useAvatar: false,
  });

  // If initialValues change (e.g. when data loads) sync into state once
  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      teamName: initialValues.teamName || "",
      kitColor: initialValues.kitColor || "#ffffff",
      logoPreview: initialValues.logoPreview || prev.logoPreview,
    }));
  }, [initialValues.teamName, initialValues.kitColor, initialValues.logoPreview]);

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (e) => {
    setFormState((prev) => ({ ...prev, kitColor: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormState((prev) => ({ ...prev, logo: file, useAvatar: false })); // selecting a logo disables avatar mode
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormState((prev) => ({ ...prev, logoPreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    setFormState((prev) => ({ ...prev, logo: null, logoPreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Notify parent whenever formState changes
  useEffect(() => {
    if (onChange) {
      const payload = {
        teamName: formState.teamName.trim(),
        kitColor: formState.kitColor,
        // When used in the sign-up flow (enableAvatar === true) we need a serialisable string,
        // not a File instance, because this object may be placed in localStorage and then
        // sent to the /auth/register JSON endpoint. For other flows we keep the File object.
        logo: enableAvatar ? (formState.logoPreview || null) : formState.logo,
        ...(enableAvatar ? { useAvatar: formState.useAvatar } : {}),
      };
      onChange(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  const internalSubmit = (e) => {
    e.preventDefault();
    if (!formState.teamName.trim()) {
      alert("Team name is required");
      return;
    }
    if (!formState.kitColor) {
      alert("Kit color is required");
      return;
    }
    // If avatar is enabled, ensure either logo or avatar chosen
    if (enableAvatar && !formState.logo && !formState.useAvatar) {
      alert("Please upload a logo or choose to use your avatar");
      return;
    }

    const payload = {
      teamName: formState.teamName.trim(),
      kitColor: formState.kitColor,
      // Convert logo to a base-64 string when in the registration wizard so that it can
      // survive JSON.stringify and be accepted by the backend.
      logo: enableAvatar ? (formState.logoPreview || null) : formState.logo, // may be null
      ...(enableAvatar ? { useAvatar: formState.useAvatar } : {}),
    };
    onSubmit && onSubmit(payload);
  };

  return (
    <form onSubmit={internalSubmit} className="team-form">
      <div className="form-group">
        <label htmlFor="teamName">TEAM NAME *</label>
        <input
          id="teamName"
          type="text"
          name="teamName"
          value={formState.teamName}
          onChange={handleTextChange}
          required
        />
      </div>

      {/* Logo upload */}
      <div className="form-group photo-group">
        <div className="photo-grid">
          <div className="upload-block">
            <h4>UPLOAD LOGO</h4>
            <label htmlFor="team_logo_input" className="read-more-btn">
              CHOOSE FILE
            </label>
            <input
              ref={fileInputRef}
              id="team_logo_input"
              accept="image/*"
              type="file"
              onChange={handleFileChange}
            />
          </div>
          <div className="file-tab">
            <div className={`tab file-block ${formState.logoPreview ? "active" : ""}`}>
              {formState.logoPreview && (
                <div className="image">
                  <div className="image-clear-btn" onClick={handleClearImage} />
                  {formState.logoPreview.startsWith('data:') ? (
                    /* Base64 preview of freshly-picked file */
                    <img src={formState.logoPreview} alt="Preview" />
                  ) : (
                    <TeamLogo logoUrl={formState.logoPreview} teamName={formState.teamName} />
                  )}
                </div>
              )}
            </div>
            {!formState.logoPreview && (
              <div className="tab default-block">
                {enableAvatar && (
                  <div className="checkbox-block round" style={{ marginTop: 10 }}>
                    <input
                      type="checkbox"
                      id="use_avatar_checkbox"
                      checked={formState.useAvatar}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormState((prev) => ({
                          ...prev,
                          useAvatar: checked,
                          ...(checked ? { logo: null, logoPreview: null } : {}),
                        }));
                        if (checked && fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    />
                    <label htmlFor="use_avatar_checkbox">Use my avatar</label>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kit color */}
      <div className="form-group color-group">
        <h4>CHOOSE KIT COLOUR *</h4>
        <ColorPicker
          data={{ kitColor: formState.kitColor }}
          handleChange={handleColorChange}
        />
      </div>

      <button type="submit" className="submit-btn" disabled={isSubmitting}>
        {submitLabel}
      </button>
    </form>
  );
}

export { TeamForm };
